// Service to interact with Cloudflare Worker for global favorites tracking
const WORKER_URL = process.env.REACT_APP_FAVORITES_WORKER_URL || 'https://favorites-tracker.zoneplace.workers.dev';

class FavoritesTracker {
  constructor() {
    this.workerUrl = WORKER_URL;
    this.userId = this.generateUserId();
  }

  // Generate a persistent user ID for tracking
  generateUserId() {
    let userId = localStorage.getItem('favoriteUserId');
    if (!userId) {
      userId = 'user_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
      localStorage.setItem('favoriteUserId', userId);
    }
    return userId;
  }

  // Track a favorite action (add/remove)
  async trackFavorite(type, id, action) {
    try {
      const response = await fetch(`${this.workerUrl}/track-favorite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: type, // 'model' or 'photoshoot'
          id: id,
          action: action, // 'add' or 'remove'
          userId: this.userId // Include user ID for tracking
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error tracking favorite:', error);
      // Don't throw - we want local favorites to continue working even if global tracking fails
      return { success: false, error: error.message };
    }
  }

  // Get most favorited items
  async getMostFavorited(type, limit = 10) {
    try {
      const response = await fetch(`${this.workerUrl}/most-favorited?type=${type}&limit=${limit}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting most favorited:', error);
      return { type, items: [] };
    }
  }

  // Get favorite count for a specific item
  async getFavoriteCount(type, id) {
    try {
      const response = await fetch(`${this.workerUrl}/favorite-count?type=${type}&id=${id}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting favorite count:', error);
      return { type, id, count: 0 };
    }
  }

  // Get multiple favorite counts at once
  async getMultipleFavoriteCounts(items) {
    const promises = items.map(item => 
      this.getFavoriteCount(item.type, item.id)
    );
    
    try {
      const results = await Promise.all(promises);
      return results.reduce((acc, result) => {
        acc[`${result.type}:${result.id}`] = result.count;
        return acc;
      }, {});
    } catch (error) {
      console.error('Error getting multiple favorite counts:', error);
      return {};
    }
  }

  // Get trending items
  async getTrending(type, limit = 10, period = 'daily') {
    try {
      const response = await fetch(`${this.workerUrl}/trending?type=${type}&limit=${limit}&period=${period}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting trending:', error);
      return { type, items: [], period: 'last 24 hours' };
    }
  }

  // Get user's favorite collections (stored locally for now)
  getCollections() {
    const collections = localStorage.getItem('favoriteCollections');
    return collections ? JSON.parse(collections) : [];
  }

  // Create a new collection
  createCollection(name, description = '') {
    const collections = this.getCollections();
    const newCollection = {
      id: Date.now().toString(),
      name,
      description,
      items: [],
      created: new Date().toISOString()
    };
    
    collections.push(newCollection);
    localStorage.setItem('favoriteCollections', JSON.stringify(collections));
    return newCollection;
  }

  // Add item to collection
  addToCollection(collectionId, type, id) {
    const collections = this.getCollections();
    const collection = collections.find(c => c.id === collectionId);
    
    if (collection) {
      const itemKey = `${type}:${id}`;
      if (!collection.items.find(item => `${item.type}:${item.id}` === itemKey)) {
        collection.items.push({ type, id: parseInt(id), addedAt: new Date().toISOString() });
        localStorage.setItem('favoriteCollections', JSON.stringify(collections));
      }
    }
  }

  // Remove item from collection
  removeFromCollection(collectionId, type, id) {
    const collections = this.getCollections();
    const collection = collections.find(c => c.id === collectionId);
    
    if (collection) {
      collection.items = collection.items.filter(item => 
        !(item.type === type && item.id === parseInt(id))
      );
      localStorage.setItem('favoriteCollections', JSON.stringify(collections));
    }
  }

  // Delete collection
  deleteCollection(collectionId) {
    const collections = this.getCollections();
    const filtered = collections.filter(c => c.id !== collectionId);
    localStorage.setItem('favoriteCollections', JSON.stringify(filtered));
  }
}

// Create singleton instance
const favoritesTracker = new FavoritesTracker();

export default favoritesTracker;
